import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import styled, { keyframes } from 'styled-components'
import { toast } from 'react-toastify'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import AddTaskRoundedIcon from '@mui/icons-material/AddTaskRounded'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded'
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded'
import BlockRoundedIcon from '@mui/icons-material/BlockRounded'
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import RuleRoundedIcon from '@mui/icons-material/RuleRounded'
import { fetchComplianceSetupOverview, logComplianceSetupAction, updateProgrammeObligation, setComplianceTracking } from 'services/ProgrammeService'
import { setObligationOverride } from 'services/BpService'
import ActionsAPI from 'services/ActionsAPI'
import LinkEvidenceModal from 'features/compliance/LinkEvidenceModal'
import LogCompletionModal from 'features/compliance/LogCompletionModal'
import TaskVsEvidenceExplainer from 'features/compliance/TaskVsEvidenceExplainer'
import PerUnitEvidenceModal from 'features/compliance/PerUnitEvidenceModal'
import { cadenceLabel } from 'features/compliance/obligationConstants'
import ComplianceSetupExplainer from 'features/compliance/ComplianceSetupExplainer'
import { openInNewTab } from 'utils/openInNewTab'
import { notifyComplianceChanged } from 'utils/complianceEvents'
import { Tooltip } from '@mui/material'
import { bp } from 'features/dashboard/tokens'

const CRIT = {
  critical: { fg: '#b00020', bg: '#fdecea', label: 'Critical' },
  high:     { fg: '#c2410c', bg: '#ffedd5', label: 'High' },
  medium:   { fg: '#d97706', bg: '#fef3c7', label: 'Medium' },
  low:      { fg: '#15803d', bg: '#dcfce7', label: 'Low' },
}

const PRIORITY_FROM_CRIT = { low: 'low', medium: 'medium', high: 'high', critical: 'critical' }

const recurrenceFromCadence = days => {
  if (!days || days <= 0) return null
  if (days % 365 === 0) return { i: days / 365, u: 'year' }
  if (days % 30 === 0) return { i: days / 30, u: 'month' }
  if (days % 7 === 0) return { i: days / 7, u: 'week' }
  return { i: Math.max(1, Math.round(days / 30)), u: 'month' }
}

const Tip = ({ title, children }) => (
  <Tooltip
    title={title}
    arrow
    placement='top'
    enterDelay={200}
    slotProps={{
      popper: { sx: { zIndex: 100060 } },
      tooltip: { sx: { bgcolor: '#0C1229', color: '#fff', fontSize: '11.5px', fontWeight: 600, lineHeight: 1.5, maxWidth: 250, px: 1.5, py: 1, borderRadius: '9px', boxShadow: '0 10px 30px rgba(12,18,41,0.28)' } },
      arrow: { sx: { color: '#0C1229' } },
    }}
  >
    <TipTarget>{children}</TipTarget>
  </Tooltip>
)

const ComplianceSetup = ({ open, onClose }) => {
  const [buildings, setBuildings] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [linkTarget, setLinkTarget] = useState(null)
  const [logTarget, setLogTarget] = useState(null)
  const [perUnitTarget, setPerUnitTarget] = useState(null)
  const [expanded, setExpanded] = useState({})
  const [busy, setBusy] = useState(null)
  const [dismissing, setDismissing] = useState(null)
  const [notTracked, setNotTracked] = useState([])

  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true)
    setError(null)
    fetchComplianceSetupOverview()
      .then(res => {
        const payload = res?.data?.data
        setBuildings(Array.isArray(payload?.buildings) ? payload.buildings : [])
        setNotTracked(Array.isArray(payload?.notTracked) ? payload.notTracked : [])
      })
      .catch(e => {
        setError(e?.response ? `${e.response.status} ${e.response.statusText || ''}`.trim() : (e?.message || 'Request failed'))
      })
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { if (open) load() }, [open, load])

  useEffect(() => {
    if (buildings.length > 0) {
      setExpanded(prev => Object.keys(prev).length === 0 ? { [buildings[0].id]: true } : prev)
    }
  }, [buildings])

  // Returning to this tab after a new-tab action (create task / upload) should
  // refresh the outstanding list so resolved items drop off.
  useEffect(() => {
    if (!open) return
    const onFocus = () => load(true)
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [open, load])

  const totalOutstanding = useMemo(
    () => buildings.reduce((n, b) => n + (b.outstandingCount ?? b.obligations?.length ?? 0), 0),
    [buildings]
  )

  const fmtDate = d => {
    if (!d) return ''
    const dt = new Date(d)
    return Number.isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  }

  const logAction = (bpId, obligationName, action) => {
    logComplianceSetupAction({ buildingPassportId: bpId, obligationName, action }).catch(() => {})
  }

  const dismissBuilding = (building) => {
    setDismissing(building.id)
    setComplianceTracking(building.id, false)
      .then(() => {
        setBuildings(prev => prev.filter(b => b.id !== building.id))
        setNotTracked(prev => prev.some(b => b.id === building.id) ? prev : [...prev, { id: building.id, name: building.name }])
        notifyComplianceChanged()
        toast.success(`Compliance tracking turned off for ${building.name}. You can turn it back on below or from its compliance tracker.`)
      })
      .catch(() => toast.error('Could not turn off compliance tracking. Please try again.'))
      .finally(() => setDismissing(null))
  }

  const reEnableBuilding = (building) => {
    setDismissing(building.id)
    setComplianceTracking(building.id, true)
      .then(() => {
        setNotTracked(prev => prev.filter(b => b.id !== building.id))
        notifyComplianceChanged()
        load(true)
        toast.success(`Compliance tracking turned back on for ${building.name}.`)
      })
      .catch(() => toast.error('Could not turn compliance tracking back on. Please try again.'))
      .finally(() => setDismissing(null))
  }

  const removeObligation = (bpId, oblId) => {
    setBuildings(prev => prev
      .map(b => {
        if (b.id !== bpId) return b
        const obligations = (b.obligations || []).filter(o => o.id !== oblId)
        const outstandingCount = obligations.filter(o => !o.openTask).length
        return {
          ...b,
          obligations,
          outstandingCount,
          setUpCount: Math.max(0, (b.totalCount ?? obligations.length) - outstandingCount),
        }
      })
      .filter(b => (b.outstandingCount ?? b.obligations?.length ?? 0) > 0))
    notifyComplianceChanged()
  }

  const createTask = (bp, obl) => {
    const params = new URLSearchParams({
      title: obl.name,
      description: `Compliance obligation${obl.regulatoryFramework ? ` under ${obl.regulatoryFramework}` : ''}.`,
      bpId: bp.id,
      source: 'compliance',
      programmeObligationId: obl.id,
    })
    if (obl.taskDomainId) params.set('taskDomainId', obl.taskDomainId)
    const priority = PRIORITY_FROM_CRIT[obl.criticality]
    if (priority) params.set('priority', priority)
    const rec = recurrenceFromCadence(obl.cadenceDays)
    if (rec) {
      params.set('recurrenceType', 'repeating')
      params.set('recurrenceInterval', String(rec.i))
      params.set('recurrenceUnit', rec.u)
      if (obl.nextDueAt) params.set('recurrenceStartDate', obl.nextDueAt)
    } else if (obl.nextDueAt) {
      params.set('deadline', obl.nextDueAt)
    }
    openInNewTab(`/tasks/create?${params.toString()}`)
  }

  const uploadNew = (bp, obl) => {
    const q = obl.fileTypeId ? `?method=traditional&type=${encodeURIComponent(obl.fileTypeId)}` : ''
    openInNewTab(`/files/upload/${bp.id}${q}`)
  }

  const raiseRemedial = async (bp, obl) => {
    setBusy(obl.id)
    try {
      await ActionsAPI.create({
        buildingPassportId: bp.id,
        programmeObligationId: obl.id,
        title: `Remedial: ${obl.name}`,
        description: (obl.description || '').slice(0, 500) || null,
        severity: obl.criticality,
      })
      toast.success(`Remedial raised on ${bp.name}. Logged to your notifications`)
      logAction(bp.id, obl.name, 'raised a remedial for')
      removeObligation(bp.id, obl.id)
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not raise the remedial.')
    } finally { setBusy(null) }
  }

  const confirmApplies = async (bp, obl) => {
    setBusy(obl.id)
    try {
      await updateProgrammeObligation(bp.id, obl.id, { isSkipped: false })
      toast.success(`Confirmed "${obl.name}" applies on ${bp.name}. It's now tracked in your compliance score`)
      logAction(bp.id, obl.name, 'confirmed applies for')
      removeObligation(bp.id, obl.id)
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not confirm this obligation.')
    } finally { setBusy(null) }
  }

  const markNotApplicable = async (bp, obl) => {
    setBusy(obl.id)
    try {
      await setObligationOverride({
        buildingPassportId: bp.id,
        librarySlug: obl.librarySlug,
        reason: 'Marked not applicable during compliance setup',
        status: 'not_applicable',
      })
      toast.success(`Marked "${obl.name}" not applicable on ${bp.name}. Logged to your notifications`)
      logAction(bp.id, obl.name, 'marked as not applicable')
      removeObligation(bp.id, obl.id)
    } catch {
      toast.error('Could not update this obligation.')
    } finally { setBusy(null) }
  }

  if (!open) return null

  return createPortal(
    <>
      <Backdrop onClick={onClose}>
        <Card onClick={e => e.stopPropagation()}>
          <Header>
            <HeaderIcon><RuleRoundedIcon style={{ fontSize: 22 }} /></HeaderIcon>
            <HeaderText>
              <Title>Finish setting up your buildings</Title>
              <Sub>
                {loading
                  ? 'Checking what still needs review…'
                  : buildings.length === 0
                    ? 'You’re all caught up. Every building has been reviewed. Nothing to do here.'
                    : `You’ve got ${totalOutstanding} obligation${totalOutstanding === 1 ? '' : 's'} to review across ${buildings.length} building${buildings.length === 1 ? '' : 's'}. For each one, just tell us whether it applies to your building. Confirm it applies, or mark it not applicable. You can add documents and tasks any time after.`}
              </Sub>
              <ExplainRow><ComplianceSetupExplainer align='left' /></ExplainRow>
            </HeaderText>
            <CloseBtn onClick={onClose} aria-label='Close'><CloseRoundedIcon style={{ fontSize: 20 }} /></CloseBtn>
          </Header>

          <Body>
            {!loading && buildings.length > 0 && (
              <Explainer>
                <ExplainerIcon><AutoAwesomeRoundedIcon style={{ fontSize: 16 }} /></ExplainerIcon>
                <span>
                  Setup is a <strong>decision</strong>, not a score. These obligations haven’t been
                  reviewed yet. Confirm each <strong>applies</strong> (it stays in your tracker to be
                  satisfied later) or mark it <strong>not applicable</strong>. Uploading a document or
                  scheduling a task is optional here and counts towards your compliance score, not setup.
                  Ones already tracked show <strong>View task</strong> and stop counting.
                </span>
                <TaskVsEvidenceExplainer />
              </Explainer>
            )}
            {loading ? (
              [0, 1, 2].map(i => <SkeletonBlock key={i} />)
            ) : error ? (
              <Empty>
                <EmptyBig style={{ color: '#b00020' }}>Couldn't load your setup</EmptyBig>
                Request failed ({error}). Check you're still signed in, then retry.
                <div><RetryBtn onClick={() => load()}>Try again</RetryBtn></div>
              </Empty>
            ) : buildings.length === 0 ? (
              <Empty>
                <EmptyIcon><CheckCircleRoundedIcon style={{ fontSize: 32 }} /></EmptyIcon>
                <EmptyBig>You’re all caught up</EmptyBig>
                Every building you manage has been reviewed. Nothing to work through here.
              </Empty>
            ) : (
              buildings.map(bp => {
                const isOpen = !!expanded[bp.id]
                return (
                <BpBlock key={bp.id}>
                  <BpHead $clickable $open={isOpen} onClick={() => setExpanded(m => ({ ...m, [bp.id]: !m[bp.id] }))}>
                    <BpCaret $open={isOpen}>›</BpCaret>
                    <BpIcon><ApartmentRoundedIcon style={{ fontSize: 17 }} /></BpIcon>
                    <BpName>{bp.name}</BpName>
                    <BpProgress title={`${bp.setUpCount ?? 0} of ${bp.totalCount ?? 0} complete`}>
                      <BpProgressFill style={{ width: `${bp.totalCount > 0 ? Math.round(((bp.setUpCount ?? 0) / bp.totalCount) * 100) : 0}%` }} />
                    </BpProgress>
                    <BpCount>{bp.outstandingCount ?? bp.obligations?.length ?? 0} to review</BpCount>
                    <Tip title='Open this building’s full compliance tracker in a new tab. The complete list with filters, history and evidence.'>
                      <OpenBpLink type='button' onClick={e => { e.stopPropagation(); openInNewTab(`/bp-list/${bp.id}/compliance`) }}>
                        Open tracker <OpenInNewRoundedIcon style={{ fontSize: 13 }} />
                      </OpenBpLink>
                    </Tip>
                    <Tip title='Turn off compliance tracking for this whole building. Everyone stops being prompted to set it up, and it won’t appear here. You can turn it back on any time from its compliance tracker.'>
                      <DontTrackBtn type='button' disabled={dismissing === bp.id} onClick={e => { e.stopPropagation(); dismissBuilding(bp) }}>
                        <BlockRoundedIcon style={{ fontSize: 13 }} /> Do not track
                      </DontTrackBtn>
                    </Tip>
                  </BpHead>
                  {isOpen && (
                  <Rows>
                    {(bp.obligations || []).map(obl => {
                      const isFile = obl.obligationKind === 'file_type'
                      const isUnit = obl.scope === 'unit'
                      const crit = CRIT[obl.criticality] || CRIT.medium
                      const b = busy === obl.id
                      const hasTask = !!obl.openTask
                      return (
                        <Row key={obl.id} $busy={b} $done={hasTask}>
                          <RowMain>
                            <RowName>{obl.name}</RowName>
                            <RowMeta>
                              <Pill $fg={crit.fg} $bg={crit.bg}>{crit.label}</Pill>
                              <KindPill $file={isFile}>{isFile ? (isUnit ? 'Per-unit document' : 'Document') : 'Task'}</KindPill>
                              {obl.cadenceDays ? <MetaText>{cadenceLabel(obl.cadenceDays)}</MetaText> : null}
                              {obl.taskDomainName ? <MetaText>{obl.taskDomainName}</MetaText> : null}
                            </RowMeta>
                            {hasTask ? (
                              <RowNote $ok>✓ Task scheduled{obl.openTask.deadline ? ` · due ${fmtDate(obl.openTask.deadline)}` : ''}. Already tracked, no action needed</RowNote>
                            ) : (
                              <RowNote>No decision yet. Confirm it applies or mark it not applicable. Adding a document or task is optional and improves your compliance score.</RowNote>
                            )}
                          </RowMain>
                          <RowActions>
                            {hasTask ? (
                              <Tip title='A task is already scheduled for this obligation. Open it in a new tab to see or reassign the work.'>
                                <ViewAct disabled={b} onClick={() => openInNewTab(`/tasks/${obl.openTask.id}`)}>
                                  <AssignmentTurnedInRoundedIcon style={{ fontSize: 15 }} /> View task
                                </ViewAct>
                              </Tip>
                            ) : (
                              <DecisionCol>
                                <DecisionRow>
                                  <Tip title='Confirm this obligation applies to this building. It stays in your compliance tracker to be satisfied. You can add the document or task any time. This finishes setup for this item.'>
                                    <PrimaryAct disabled={b} onClick={() => confirmApplies(bp, obl)}>
                                      <CheckCircleRoundedIcon style={{ fontSize: 15 }} /> Yes, this applies
                                    </PrimaryAct>
                                  </Tip>
                                  <Tip title='Not applicable. This obligation doesn’t apply to this building, so exclude it from the compliance score.'>
                                    <Act disabled={b} onClick={() => markNotApplicable(bp, obl)}>
                                      <BlockRoundedIcon style={{ fontSize: 15 }} /> Not applicable
                                    </Act>
                                  </Tip>
                                </DecisionRow>
                                <OptionalRow>
                                  <OptionalLabel>Optional, do it now:</OptionalLabel>
                                  {isFile ? (
                                    isUnit ? (
                                      <Tip title='Add a document for every unit this obligation applies to. Evidence is stored on each Unit Passport.'>
                                        <MiniAct disabled={b} onClick={() => setPerUnitTarget({ bpId: bp.id, obligation: obl })}>
                                          <ApartmentRoundedIcon style={{ fontSize: 14 }} /> Add unit evidence
                                        </MiniAct>
                                      </Tip>
                                    ) : (
                                      <>
                                        <Tip title='Attach a document already uploaded to this building as the evidence for this obligation.'>
                                          <MiniAct disabled={b} onClick={() => setLinkTarget({ bpId: bp.id, obligation: obl })}>
                                            <DescriptionRoundedIcon style={{ fontSize: 14 }} /> Select file
                                          </MiniAct>
                                        </Tip>
                                        <Tip title='Upload a new document for this obligation. Opens the upload page in a new tab, where it’s auto-classified and linked back as evidence.'>
                                          <MiniAct disabled={b} onClick={() => uploadNew(bp, obl)}>
                                            <UploadFileRoundedIcon style={{ fontSize: 14 }} /> Upload
                                          </MiniAct>
                                        </Tip>
                                      </>
                                    )
                                  ) : bp.canManageTasks ? (
                                    <Tip title='Schedule the recurring work that keeps this obligation satisfied. Opens the task builder in a new tab, pre-filled with the right cadence.'>
                                      <MiniAct disabled={b} onClick={() => createTask(bp, obl)}>
                                        <AddTaskRoundedIcon style={{ fontSize: 14 }} /> Create task
                                      </MiniAct>
                                    </Tip>
                                  ) : null}
                                  <Tip title='Record that this work has already been done, with the date it was completed. Satisfies the obligation without scheduling a task.'>
                                    <MiniAct disabled={b} onClick={() => setLogTarget({ bpId: bp.id, obligation: obl })}>
                                      <EventAvailableRoundedIcon style={{ fontSize: 14 }} /> Mark done
                                    </MiniAct>
                                  </Tip>
                                  {bp.canManageTasks && (
                                    <Tip title='Raise a remedial to flag and track fixing a deficiency on this obligation.'>
                                      <MiniAct disabled={b} onClick={() => raiseRemedial(bp, obl)}>
                                        <ReportProblemRoundedIcon style={{ fontSize: 14 }} /> Remedial
                                      </MiniAct>
                                    </Tip>
                                  )}
                                </OptionalRow>
                              </DecisionCol>
                            )}
                          </RowActions>
                        </Row>
                      )
                    })}
                  </Rows>
                  )}
                </BpBlock>
                )
              })
            )}

            {notTracked.length > 0 && (
              <NotTrackedSection>
                <NotTrackedHead>Not tracked ({notTracked.length})</NotTrackedHead>
                <NotTrackedHint>Compliance isn’t being tracked for these buildings, so they’re hidden from setup. Turn it back on to include them again.</NotTrackedHint>
                {notTracked.map(b => (
                  <NotTrackedRow key={b.id}>
                    <NotTrackedName title={b.name}>{b.name}</NotTrackedName>
                    <ReEnableBtn type='button' disabled={dismissing === b.id} onClick={() => reEnableBuilding(b)}>
                      {dismissing === b.id ? 'Turning on…' : 'Turn on tracking'}
                    </ReEnableBtn>
                  </NotTrackedRow>
                ))}
              </NotTrackedSection>
            )}
          </Body>

          <Footer>
            <FootNote>No rush. Do a few now and come back any time from the compliance banner on your dashboard.</FootNote>
            <DoneBtn onClick={onClose}>Continue later</DoneBtn>
          </Footer>
        </Card>
      </Backdrop>

      {linkTarget && (
        <LinkEvidenceModal
          bpId={linkTarget.bpId}
          obligation={linkTarget.obligation}
          onClose={() => setLinkTarget(null)}
          onLinked={() => {
            logAction(linkTarget.bpId, linkTarget.obligation.name, 'attached evidence to')
            removeObligation(linkTarget.bpId, linkTarget.obligation.id)
            setLinkTarget(null)
          }}
        />
      )}
      {logTarget && (
        <LogCompletionModal
          bpId={logTarget.bpId}
          obligation={logTarget.obligation}
          onClose={() => setLogTarget(null)}
          onLogged={() => {
            logAction(logTarget.bpId, logTarget.obligation.name, 'logged completion of')
            removeObligation(logTarget.bpId, logTarget.obligation.id)
            setLogTarget(null)
          }}
        />
      )}
      {perUnitTarget && (
        <PerUnitEvidenceModal
          bpId={perUnitTarget.bpId}
          obligation={perUnitTarget.obligation}
          onClose={() => setPerUnitTarget(null)}
          onChanged={() => { load(true); notifyComplianceChanged() }}
        />
      )}
    </>,
    document.body
  )
}

export default ComplianceSetup

const fadeIn = keyframes`from { opacity: 0 } to { opacity: 1 }`
const rise = keyframes`from { opacity: 0; transform: translateY(12px) } to { opacity: 1; transform: none }`

const Backdrop = styled.div`
  position: fixed; inset: 0; z-index: 100000;
  background: rgba(12, 18, 41, 0.55);
  display: flex; align-items: center; justify-content: center;
  padding: 24px; box-sizing: border-box;
  animation: ${fadeIn} 0.2s ease;
  @media (max-width: ${bp.mobile}) {
    padding: 0; align-items: flex-end;
  }
`
const Card = styled.div`
  width: 100%; max-width: 900px; max-height: 88vh;
  display: flex; flex-direction: column;
  background: #fff; border-radius: 16px; overflow: hidden;
  box-shadow: 0 24px 64px rgba(12, 18, 41, 0.28);
  opacity: 1;
  animation: ${rise} 0.24s cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
  @media (max-width: ${bp.mobile}) {
    max-width: none; height: 100dvh; max-height: 100dvh; border-radius: 0;
  }
`
const Header = styled.header`
  display: flex; align-items: flex-start; gap: 16px; flex-shrink: 0;
  padding: 20px 22px; border-bottom: 1px solid #eef0f3;
  @media (max-width: ${bp.mobile}) {
    gap: 10px; padding: 12px 14px;
  }
`
const HeaderIcon = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 44px; height: 44px; border-radius: 12px; flex-shrink: 0;
  background: #e6f7f9; color: #0e7490;
  @media (max-width: ${bp.mobile}) { width: 34px; height: 34px; border-radius: 10px; }
`
const HeaderText = styled.div` flex: 1; min-width: 0; `
const Title = styled.h2`
  margin: 0 0 4px; font-size: 20px; font-weight: 800; color: #101828; letter-spacing: -0.2px;
  @media (max-width: ${bp.mobile}) { font-size: 16px; line-height: 1.25; }
`
const Sub = styled.p`
  margin: 0; font-size: 13.5px; line-height: 1.5; color: #667085;
  @media (max-width: ${bp.mobile}) { font-size: 12px; line-height: 1.45; }
`
const ExplainRow = styled.div` margin-top: 8px; @media (max-width: ${bp.mobile}) { margin-top: 6px; } `
const CloseBtn = styled.button`
  border: none; background: #f2f4f7; color: #475467; cursor: pointer;
  width: 32px; height: 32px; border-radius: 8px; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  &:hover { background: #e6e9ee; }
`
const Body = styled.div`
  flex: 1 1 auto; min-height: 0; padding: 16px 22px; overflow-y: auto;
  display: flex; flex-direction: column; gap: 18px;
  -webkit-overflow-scrolling: touch; overscroll-behavior: contain;
  @media (max-width: ${bp.mobile}) { padding: 12px 12px 16px; gap: 12px; }
`
const Explainer = styled.div`
  display: flex; align-items: flex-start; gap: 10px; flex-shrink: 0;
  padding: 12px 14px; border-radius: 12px;
  background: #f2fbfc; border: 1px solid #cdeef1;
  font-size: 12.5px; line-height: 1.55; color: #134e4a;
  strong { font-weight: 700; color: #0f766e; }
  @media (max-width: ${bp.mobile}) {
    flex-wrap: wrap; gap: 8px; padding: 10px 12px; font-size: 11.5px; line-height: 1.5;
    > span { flex: 1 1 100%; order: 2; }
    > *:last-child { order: 3; flex: 1 1 100%; }
  }
`
const ExplainerIcon = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 26px; height: 26px; border-radius: 7px; flex-shrink: 0;
  background: #d3f0f3; color: #0e7490;
  @media (max-width: ${bp.mobile}) { width: 22px; height: 22px; border-radius: 6px; order: 1; }
`
const SkeletonBlock = styled.div`
  height: 120px; border-radius: 12px; background: #f4f5f7;
  animation: ${keyframes`0%,100%{opacity:0.6}50%{opacity:1}`} 1.4s ease-in-out infinite;
`
const Empty = styled.div` text-align: center; padding: 48px 16px; color: #667085; font-size: 14px; `
const EmptyIcon = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 56px; height: 56px; border-radius: 50%; margin-bottom: 14px;
  background: #dcfce7; color: #15803d;
`
const EmptyBig = styled.div` font-size: 18px; font-weight: 800; color: #15803d; margin-bottom: 6px; `
const RetryBtn = styled.button`
  margin-top: 14px; border: 1px solid #2aa6b3; background: #2aa6b3; color: #fff;
  font-size: 13px; font-weight: 700; padding: 8px 16px; border-radius: 8px; cursor: pointer;
  &:hover { filter: brightness(0.95); }
`
const BpBlock = styled.div`
  border: 1px solid #e9ebef; border-radius: 12px; overflow: hidden; flex-shrink: 0;
  transition: box-shadow 160ms ease, border-color 160ms ease;
  &:hover { border-color: #dbe3ea; box-shadow: 0 2px 8px rgba(12, 18, 41, 0.05); }
`
const BpHead = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 11px 14px; background: #f8f9fb;
  border-bottom: ${({ $open }) => ($open ? '1px solid #eef0f3' : 'none')};
  ${({ $clickable }) => ($clickable ? 'cursor: pointer;' : '')}
  &:hover { background: #f2f4f7; }
  @media (max-width: ${bp.mobile}) { flex-wrap: wrap; gap: 8px; padding: 10px 12px; }
`
const BpCaret = styled.span`
  color: #98a2b3; font-size: 18px; line-height: 1; flex-shrink: 0;
  transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')});
  transition: transform 0.15s ease;
`
const BpIcon = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 28px; height: 28px; border-radius: 8px; background: #e6f4f6; color: #0e7490; flex-shrink: 0;
`
const BpName = styled.span` font-size: 14px; font-weight: 800; color: #1d2433; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; `
const BpCount = styled.span` font-size: 11.5px; font-weight: 700; color: #b54708; background: #fef3c7; padding: 3px 9px; border-radius: 999px; white-space: nowrap; `
const BpProgress = styled.div`
  width: 88px; height: 6px; border-radius: 999px; background: #eaecef; overflow: hidden; flex-shrink: 0;
  @media (max-width: ${bp.mobile}) { width: 100%; order: 3; flex-basis: 100%; }
`
const BpProgressFill = styled.div` height: 100%; background: #15803d; border-radius: 999px; transition: width 0.3s ease; `
const OpenBpLink = styled.button`
  font: inherit; border: none; background: none; cursor: pointer;
  display: inline-flex; align-items: center; gap: 3px;
  font-size: 12px; font-weight: 700; color: #2aa6b3; white-space: nowrap;
  &:hover { text-decoration: underline; }
`
const DontTrackBtn = styled.button`
  font: inherit; border: 1px solid #e5e7eb; background: #fff; cursor: pointer;
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 9px; border-radius: 7px;
  font-size: 11.5px; font-weight: 600; color: #6b7280; white-space: nowrap;
  transition: color 0.15s, border-color 0.15s, background 0.15s;
  &:hover:not(:disabled) { color: #b00020; border-color: #f3c9c9; background: #fdf4f4; }
  &:disabled { opacity: 0.5; cursor: default; }
  @media (max-width: ${bp.mobile}) { order: 4; }
`
const NotTrackedSection = styled.div`
  margin: 8px 14px 4px; padding: 14px; border: 1px dashed #e0e3e8; border-radius: 10px; background: #fafbfc;
`
const NotTrackedHead = styled.div` font-size: 12px; font-weight: 800; letter-spacing: 0.03em; text-transform: uppercase; color: #64748b; `
const NotTrackedHint = styled.div` font-size: 12px; color: #64748b; line-height: 1.5; margin: 3px 0 10px; `
const NotTrackedRow = styled.div`
  display: flex; align-items: center; gap: 12px; padding: 8px 0; border-top: 1px solid #eef0f3;
  &:first-of-type { border-top: none; }
`
const NotTrackedName = styled.span` flex: 1; min-width: 0; font-size: 13px; font-weight: 600; color: #1d2433; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; `
const ReEnableBtn = styled.button`
  flex-shrink: 0; font: inherit; cursor: pointer; white-space: nowrap;
  padding: 6px 12px; border-radius: 7px; border: 1px solid #33becc; background: #fff;
  font-size: 12px; font-weight: 700; color: #2aa6b3;
  transition: background 0.15s, color 0.15s;
  &:hover:not(:disabled) { background: #33becc; color: #fff; }
  &:disabled { opacity: 0.55; cursor: default; }
`
const Rows = styled.div` display: flex; flex-direction: column; `
const Row = styled.div`
  display: flex; align-items: center; gap: 14px;
  padding: 12px 14px; border-bottom: 1px solid #f2f3f5;
  opacity: ${({ $busy }) => ($busy ? 0.5 : 1)};
  background: ${({ $done }) => ($done ? '#fbfefc' : 'transparent')};
  &:last-child { border-bottom: none; }
  @media (max-width: ${bp.mobile}) { flex-direction: column; align-items: stretch; gap: 10px; padding: 12px; }
`
const RowMain = styled.div` flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 5px; `
const RowName = styled.span` font-size: 13.5px; font-weight: 600; color: #1d2433; `
const RowNote = styled.span` font-size: 11.5px; color: ${({ $ok }) => ($ok ? '#15803d' : '#b54708')}; `
const RowMeta = styled.div` display: flex; align-items: center; gap: 7px; flex-wrap: wrap; `
const Pill = styled.span` font-size: 10.5px; font-weight: 700; padding: 2px 8px; border-radius: 999px; color: ${({ $fg }) => $fg}; background: ${({ $bg }) => $bg}; `
const KindPill = styled.span` font-size: 10.5px; font-weight: 700; padding: 2px 8px; border-radius: 999px; color: ${({ $file }) => ($file ? '#0e7490' : '#c2410c')}; background: ${({ $file }) => ($file ? '#e0f2fe' : '#ffedd5')}; `
const MetaText = styled.span` font-size: 11.5px; color: #8a909b; `
const RowActions = styled.div`
  display: flex; align-items: center; gap: 6px; flex-shrink: 0; flex-wrap: wrap;
  @media (max-width: ${bp.mobile}) { width: 100%; > span { flex: 1 1 100%; } }
`
const TipTarget = styled.span` display: inline-flex; @media (max-width: ${bp.mobile}) { min-width: 0; } `
const baseAct = `
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 12px; font-weight: 700; padding: 7px 11px; border-radius: 8px;
  cursor: pointer; white-space: nowrap; transition: all 0.12s ease;
  &:disabled { opacity: 0.5; cursor: default; }
`
const PrimaryAct = styled.button`
  ${baseAct}
  border: 1px solid #2aa6b3; background: #2aa6b3; color: #fff;
  &:hover:not(:disabled) { filter: brightness(0.95); }
`
const Act = styled.button`
  ${baseAct}
  border: 1px solid #dfe3e8; background: #fff; color: #475467;
  &:hover:not(:disabled) { background: #f7f8fa; }
`
const ViewAct = styled.button`
  ${baseAct}
  border: 1px solid #b9e6c9; background: #f0fbf4; color: #15803d;
  &:hover:not(:disabled) { background: #e3f6ea; }
  @media (max-width: ${bp.mobile}) { width: 100%; justify-content: center; padding: 10px 12px; }
`
const DecisionCol = styled.div`
  display: flex; flex-direction: column; gap: 8px; align-items: flex-end;
  @media (max-width: ${bp.mobile}) { align-items: stretch; width: 100%; }
`
const DecisionRow = styled.div`
  display: flex; gap: 6px; flex-wrap: wrap; justify-content: flex-end;
  @media (max-width: ${bp.mobile}) {
    justify-content: stretch; gap: 8px;
    > * { flex: 1 1 0; min-width: 0; }
    button { width: 100%; justify-content: center; padding: 10px 8px; }
  }
`
const OptionalRow = styled.div`
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap; justify-content: flex-end;
  @media (max-width: ${bp.mobile}) { justify-content: flex-start; }
`
const OptionalLabel = styled.span` font-size: 10.5px; font-weight: 700; color: #98a2b3; `
const MiniAct = styled.button`
  ${baseAct}
  font-size: 11px; font-weight: 600; padding: 5px 9px;
  border: 1px solid #e4e7ec; background: #fff; color: #667085;
  &:hover:not(:disabled) { background: #f7f8fa; }
  @media (max-width: ${bp.mobile}) { padding: 8px 10px; font-size: 11.5px; }
`
const Footer = styled.footer`
  display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-shrink: 0;
  padding: 14px 22px; border-top: 1px solid #eef0f3; background: #fcfcfd;
  @media (max-width: ${bp.mobile}) {
    flex-direction: column-reverse; align-items: stretch; gap: 8px;
    padding: 10px 12px calc(10px + env(safe-area-inset-bottom));
  }
`
const FootNote = styled.span`
  font-size: 12px; color: #8a909b;
  @media (max-width: ${bp.mobile}) { font-size: 11px; text-align: center; line-height: 1.4; }
`
const DoneBtn = styled.button`
  border: 1px solid #101828; background: #101828; color: #fff;
  font-size: 13px; font-weight: 700; padding: 9px 18px; border-radius: 9px; cursor: pointer;
  &:hover { background: #1d2433; }
  @media (max-width: ${bp.mobile}) { width: 100%; padding: 12px 18px; }
`
